/**
 * Per-Session State Management with Jotai
 *
 * Uses atomFamily to create isolated atoms per session.
 * Updates to one session don't trigger re-renders in other sessions.
 *
 * This solves the performance issue where streaming in Session A
 * caused re-renders and focus loss in Session B.
 */

import { atom } from 'jotai'
import type { Getter, Setter } from 'jotai/vanilla'
import { atomFamily } from 'jotai-family'
import type { Session, Message } from '../../shared/types'
import type { SessionConfig } from '@okclawpro/shared/sessions'

type DelegationAttachmentGrantSummary = NonNullable<SessionConfig['delegationAttachmentGrants']>[number]
type DelegationAttachmentRequestItem = NonNullable<SessionConfig['delegationAttachmentRequests']>[number]

/**
 * Session metadata for list display (lightweight, no messages)
 * Used by SessionList to avoid re-rendering on message changes
 */
export interface SessionMeta {
  id: string
  name?: string
  /** Preview of first user message (for title fallback) */
  preview?: string
  workspaceId: string
  lastMessageAt?: number
  isProcessing?: boolean
  processingGeneration?: number
  isFlagged?: boolean
  lastReadMessageId?: string
  workingDirectory?: string
  enabledSourceSlugs?: string[]
  /** Shared viewer URL (if shared via viewer) */
  sharedUrl?: string
  /** Shared session ID in viewer (for revoke) */
  sharedId?: string
  /** Shared session access type */
  sharedAccessType?: 'public' | 'password'
  /** Password share extract code */
  sharedExtractCode?: string
  /** ID of the last final (non-intermediate) assistant message - for unread detection */
  lastFinalMessageId?: string
  /**
   * Explicit unread flag - single source of truth for NEW badge.
   * Set to true when assistant message completes while user is NOT viewing.
   * Set to false when user views the session (and not processing).
   */
  hasUnread?: boolean
  /** Labels for filtering (additive tags, many-per-session) */
  labels?: string[]
  /** Permission mode ('safe', 'ask', 'allow-all') — used by view expressions */
  permissionMode?: string
  /** Session status for filtering */
  sessionStatus?: string
  /** Role/type of the last message (for badge display without loading messages) */
  lastMessageRole?: 'user' | 'assistant' | 'plan' | 'tool' | 'error'
  /** Whether an async operation is ongoing (sharing, updating share, revoking, title regeneration) */
  isAsyncOperationOngoing?: boolean
  /** @deprecated Use isAsyncOperationOngoing instead */
  isRegeneratingTitle?: boolean
  /** Model override for this session */
  model?: string
  /** LLM connection slug for this session */
  llmConnection?: string
  pendingModelRouteSwitch?: SessionConfig['pendingModelRouteSwitch']
  modelRouteRevision?: number
  piTranscriptCheckpoint?: string
  /** Token usage stats (from JSONL header, available without loading messages) */
  tokenUsage?: {
    inputTokens: number
    outputTokens: number
    totalTokens: number
    costUsd: number
    contextTokens: number
  }
  /** When the session was created (ms timestamp) */
  createdAt?: number
  /** Total number of messages in this session */
  messageCount?: number
  /** When true, session is hidden from session list (e.g., mini edit sessions) */
  hidden?: boolean
  /** Whether this session is archived */
  isArchived?: boolean
  /** Timestamp when session was archived (for retention policy) */
  archivedAt?: number
  /** Workspace-scoped project id this session is bound to (undefined = unbound) */
  projectId?: string
  /** Kanban board column id ('todo' | 'in-progress' | 'done'); independent of sessionStatus */
  kanbanColumn?: string
  /** Tasks Conductor: slug of the task spec this session belongs to (orchestrator + child nodes) */
  taskSlug?: string
  /** Tasks Conductor: id of the run that spawned this child session (Conductor-owned children only) */
  taskRunId?: string
  /** Tasks Conductor: id of the DAG node this child session executes (Conductor-owned children only) */
  taskNodeId?: string
  /** Tasks Conductor: total DAG node count (orchestrator only) — stable board progress denominator while children spawn lazily */
  taskNodeCount?: number
  /** Tasks Conductor: a generate-time draft orchestrator, hidden from the board until adopted by createTask. */
  taskDraft?: boolean
  branchFromMessageId?: string
  branchFromSessionId?: string
  branchContextStrategy?: 'sdk-fork' | 'seeded-fresh-session'
  branchSeedApplied?: boolean
  triggeredBy?: { automationName?: string; automationId?: string; event?: string; timestamp?: number }
  parentSessionId?: string
  rootSessionId?: string
  sourceDelegationId?: string
  delegationStatus?: 'running' | 'completed' | 'failed' | 'cancelled' | 'interrupted'
  delegationGoal?: string
  delegationCreatedAt?: number
  delegationCompletedAt?: number
  delegationResultSummary?: string
  delegationBatchId?: string
  delegationBatchIndex?: number
  delegationBatchCreatedAt?: number
  delegationRound?: number
  delegationLastActivityAt?: number
  delegationLastParentInstruction?: string
  delegationContinuationCount?: number
  delegationBlockedReason?: string
  delegationAttachmentGrants?: DelegationAttachmentGrantSummary[]
  delegationAttachmentRequests?: DelegationAttachmentRequestItem[]
}

type RendererDelegationV2Fields = Pick<
  SessionMeta,
  | 'delegationRound'
  | 'delegationBatchId'
  | 'delegationBatchIndex'
  | 'delegationBatchCreatedAt'
  | 'delegationLastActivityAt'
  | 'delegationLastParentInstruction'
  | 'delegationContinuationCount'
  | 'delegationBlockedReason'
  | 'delegationAttachmentGrants'
  | 'delegationAttachmentRequests'
>

type SessionWithRendererDelegationV2 = Session & Partial<RendererDelegationV2Fields>

function getDelegationV2Fields(session: Session | null | undefined): Partial<RendererDelegationV2Fields> {
  if (!session) return {}
  const source = session as SessionWithRendererDelegationV2
  return {
    delegationRound: source.delegationRound,
    delegationBatchId: source.delegationBatchId,
    delegationBatchIndex: source.delegationBatchIndex,
    delegationBatchCreatedAt: source.delegationBatchCreatedAt,
    delegationLastActivityAt: source.delegationLastActivityAt,
    delegationLastParentInstruction: source.delegationLastParentInstruction,
    delegationContinuationCount: source.delegationContinuationCount,
    delegationBlockedReason: source.delegationBlockedReason,
    delegationAttachmentGrants: source.delegationAttachmentGrants,
    delegationAttachmentRequests: source.delegationAttachmentRequests,
  }
}

function mergeDelegationList<T>(existing: T[] | undefined, incoming: T[] | undefined, preferExisting = false): T[] | undefined {
  if (preferExisting && existing !== undefined) return existing
  if (incoming === undefined) return existing
  if (incoming.length === 0 && existing && existing.length > 0) return existing
  return incoming
}

/**
 * Find the last final (non-intermediate) assistant or plan message ID
 */
function findLastFinalMessageId(messages: Message[]): string | undefined {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i]
    // Include plan messages as final responses (they're AI-generated content)
    if ((msg.role === 'assistant' || msg.role === 'plan') && !msg.isIntermediate) {
      return msg.id
    }
  }
  return undefined
}

function getSessionMessages(session: Session): Message[] {
  return Array.isArray(session.messages) ? session.messages : []
}

function ensureSessionMessagesArray(session: Session): Session {
  return Array.isArray(session.messages) ? session : { ...session, messages: [] }
}

const TERMINAL_DELEGATION_STATUSES = new Set([
  'completed',
  'failed',
  'cancelled',
  'interrupted',
] as const)

const OPTIMISTIC_SEND_GRACE_MS = 15_000

function isTerminalDelegationStatus(status: Session['delegationStatus']): boolean {
  if (!status || status === 'running') return false
  return TERMINAL_DELEGATION_STATUSES.has(status)
}

function compareDelegationLifecycle(left: Session, right: Session): number {
  const leftFields = getDelegationV2Fields(left)
  const rightFields = getDelegationV2Fields(right)
  const leftRevision = [
    leftFields.delegationRound ?? 0,
    leftFields.delegationContinuationCount ?? 0,
    leftFields.delegationLastActivityAt ?? 0,
  ]
  const rightRevision = [
    rightFields.delegationRound ?? 0,
    rightFields.delegationContinuationCount ?? 0,
    rightFields.delegationLastActivityAt ?? 0,
  ]

  for (let index = 0; index < leftRevision.length; index++) {
    if (leftRevision[index] !== rightRevision[index]) {
      return leftRevision[index] - rightRevision[index]
    }
  }
  return 0
}

/**
 * Merge a freshly fetched session snapshot with renderer-local state.
 *
 * Delegation lifecycle events can arrive before an async session snapshot resolves.
 * A stale snapshot that still says "running" must never overwrite a terminal child
 * status already observed by the renderer.
 */
export function mergeSessionSnapshot(existing: Session | null | undefined, incoming: Session): Session {
  const normalizedIncoming = ensureSessionMessagesArray(incoming)
  if (!existing) return normalizedIncoming
  if (existing.workspaceId && normalizedIncoming.workspaceId && existing.workspaceId !== normalizedIncoming.workspaceId) {
    return normalizedIncoming
  }

  const existingGeneration = existing.processingGeneration
  const incomingGeneration = normalizedIncoming.processingGeneration
  const incomingIsOlder = existingGeneration !== undefined
    && incomingGeneration !== undefined
    && incomingGeneration < existingGeneration
  const incomingIsNewer = incomingGeneration !== undefined
    && (existingGeneration === undefined || incomingGeneration > existingGeneration)
  const incomingMessages = getSessionMessages(normalizedIncoming)
  const hasUnconfirmedOptimisticUserMessage = getSessionMessages(existing).some((message) => {
    if (message.role !== 'user' || !message.isPending) return false
    if (typeof message.timestamp !== 'number' || Date.now() - message.timestamp > OPTIMISTIC_SEND_GRACE_MS) return false
    return !incomingMessages.some(incomingMessage =>
      incomingMessage.role === 'user'
      && (
        incomingMessage.id === message.id
        || (
          incomingMessage.content === message.content
          && typeof incomingMessage.timestamp === 'number'
          && typeof message.timestamp === 'number'
          && Math.abs(incomingMessage.timestamp - message.timestamp) < 5000
        )
      ),
    )
  })
  const existingDelegationV2 = getDelegationV2Fields(existing)
  const incomingDelegationV2 = getDelegationV2Fields(normalizedIncoming)
  const existingTerminal = isTerminalDelegationStatus(existing.delegationStatus)
  const incomingTerminal = isTerminalDelegationStatus(normalizedIncoming.delegationStatus)
  const incomingDelegationIsNewer = compareDelegationLifecycle(normalizedIncoming, existing) > 0
  const preserveExistingDelegation = incomingIsOlder
    || (!incomingIsNewer && !incomingDelegationIsNewer && existingTerminal && !incomingTerminal)
  const isProcessing = incomingIsOlder
    ? existing.isProcessing
    : incomingIsNewer
      ? normalizedIncoming.isProcessing
      : hasUnconfirmedOptimisticUserMessage && existing.isProcessing && !normalizedIncoming.isProcessing
        ? true
        : existing.isProcessing && normalizedIncoming.isProcessing
  const currentStatus = incomingIsOlder
    ? existing.currentStatus
    : incomingIsNewer || !isProcessing
      ? normalizedIncoming.currentStatus
      : normalizedIncoming.currentStatus ?? existing.currentStatus
  const activeResponseRun = incomingIsOlder
    ? existing.activeResponseRun
    : incomingIsNewer || !isProcessing
      ? normalizedIncoming.activeResponseRun
      : normalizedIncoming.activeResponseRun ?? existing.activeResponseRun

  return {
    ...normalizedIncoming,
    messages: incomingIsOlder
      || (existing.isProcessing && normalizedIncoming.isProcessing && !incomingIsNewer)
      || hasUnconfirmedOptimisticUserMessage
      ? getSessionMessages(existing)
      : incomingMessages,
    processingGeneration: incomingIsOlder
      ? existingGeneration
      : incomingGeneration ?? existingGeneration,
    currentStatus,
    activeResponseRun,
    parentSessionId: preserveExistingDelegation
      ? existing.parentSessionId ?? normalizedIncoming.parentSessionId
      : normalizedIncoming.parentSessionId ?? existing.parentSessionId,
    rootSessionId: preserveExistingDelegation
      ? existing.rootSessionId ?? normalizedIncoming.rootSessionId
      : normalizedIncoming.rootSessionId ?? existing.rootSessionId,
    sourceDelegationId: preserveExistingDelegation
      ? existing.sourceDelegationId ?? normalizedIncoming.sourceDelegationId
      : normalizedIncoming.sourceDelegationId ?? existing.sourceDelegationId,
    delegationStatus: preserveExistingDelegation
      ? existing.delegationStatus
      : normalizedIncoming.delegationStatus ?? existing.delegationStatus,
    delegationGoal: preserveExistingDelegation
      ? existing.delegationGoal ?? normalizedIncoming.delegationGoal
      : normalizedIncoming.delegationGoal ?? existing.delegationGoal,
    delegationCreatedAt: preserveExistingDelegation
      ? existing.delegationCreatedAt ?? normalizedIncoming.delegationCreatedAt
      : normalizedIncoming.delegationCreatedAt ?? existing.delegationCreatedAt,
    delegationCompletedAt: preserveExistingDelegation
      ? existing.delegationCompletedAt ?? normalizedIncoming.delegationCompletedAt
      : normalizedIncoming.delegationCompletedAt,
    delegationResultSummary: preserveExistingDelegation
      ? existing.delegationResultSummary ?? normalizedIncoming.delegationResultSummary
      : normalizedIncoming.delegationResultSummary,
    delegationBatchId: preserveExistingDelegation
      ? existingDelegationV2.delegationBatchId ?? incomingDelegationV2.delegationBatchId
      : incomingDelegationV2.delegationBatchId ?? existingDelegationV2.delegationBatchId,
    delegationBatchIndex: preserveExistingDelegation
      ? existingDelegationV2.delegationBatchIndex ?? incomingDelegationV2.delegationBatchIndex
      : incomingDelegationV2.delegationBatchIndex ?? existingDelegationV2.delegationBatchIndex,
    delegationBatchCreatedAt: preserveExistingDelegation
      ? existingDelegationV2.delegationBatchCreatedAt ?? incomingDelegationV2.delegationBatchCreatedAt
      : incomingDelegationV2.delegationBatchCreatedAt ?? existingDelegationV2.delegationBatchCreatedAt,
    delegationRound: preserveExistingDelegation
      ? existingDelegationV2.delegationRound ?? incomingDelegationV2.delegationRound
      : incomingDelegationV2.delegationRound ?? existingDelegationV2.delegationRound,
    delegationLastActivityAt: preserveExistingDelegation
      ? existingDelegationV2.delegationLastActivityAt ?? incomingDelegationV2.delegationLastActivityAt
      : incomingDelegationV2.delegationLastActivityAt ?? existingDelegationV2.delegationLastActivityAt,
    delegationLastParentInstruction: preserveExistingDelegation
      ? existingDelegationV2.delegationLastParentInstruction ?? incomingDelegationV2.delegationLastParentInstruction
      : incomingDelegationV2.delegationLastParentInstruction ?? existingDelegationV2.delegationLastParentInstruction,
    delegationContinuationCount: preserveExistingDelegation
      ? existingDelegationV2.delegationContinuationCount ?? incomingDelegationV2.delegationContinuationCount
      : incomingDelegationV2.delegationContinuationCount ?? existingDelegationV2.delegationContinuationCount,
    delegationBlockedReason: preserveExistingDelegation
      ? existingDelegationV2.delegationBlockedReason ?? incomingDelegationV2.delegationBlockedReason
      : incomingDelegationV2.delegationBlockedReason,
    delegationAttachmentGrants: preserveExistingDelegation
      ? mergeDelegationList(existingDelegationV2.delegationAttachmentGrants, incomingDelegationV2.delegationAttachmentGrants, true)
      : mergeDelegationList(existingDelegationV2.delegationAttachmentGrants, incomingDelegationV2.delegationAttachmentGrants),
    delegationAttachmentRequests: preserveExistingDelegation
      ? mergeDelegationList(existingDelegationV2.delegationAttachmentRequests, incomingDelegationV2.delegationAttachmentRequests, true)
      : mergeDelegationList(existingDelegationV2.delegationAttachmentRequests, incomingDelegationV2.delegationAttachmentRequests),
    isProcessing: Boolean(incomingIsOlder
      ? existing.isProcessing
      : preserveExistingDelegation && existingTerminal
        ? false
        : incomingTerminal
          ? false
          : isProcessing),
  }
}

/**
 * Extract metadata from a full session object
 */
export function extractSessionMeta(session: Session): SessionMeta {
  const messages = getSessionMessages(session)

  // Destructure fields that don't exist on SessionMeta or need overrides
  const {
    messages: _msgs, sessionFolderPath: _sf, supportsBranching: _sb,
    workspaceName: _wn, thinkingLevel: _tl, currentStatus: _cs,
    isAsyncOperationOngoing, isRegeneratingTitle,
    messageCount, lastFinalMessageId: sessionLastFinal,
    ...sessionFields
  } = session

  return {
    ...sessionFields,
    ...getDelegationV2Fields(session),
    lastFinalMessageId: sessionLastFinal ?? findLastFinalMessageId(messages),
    // Math.max, not ??: streaming appends grow `messages` without touching the
    // session's `messageCount` field, so a defined-but-stale count (stamped at
    // load/creation) must never shadow the live length. Meta-only sessions
    // (empty `messages`) keep the server/header count.
    messageCount: Math.max(messageCount ?? 0, messages.length),
    isAsyncOperationOngoing: isAsyncOperationOngoing ?? isRegeneratingTitle,
    isRegeneratingTitle,
  } as SessionMeta
}

/**
 * Atom family for individual session state
 * Each session gets its own atom - updates are isolated
 */
export const sessionAtomFamily = atomFamily(
  (_sessionId: string) => atom<Session | null>(null),
  (a, b) => a === b
)

/**
 * Atom for session metadata map (for list display)
 * Only contains lightweight data needed for SessionList
 */
export const sessionMetaMapAtom = atom<Map<string, SessionMeta>>(new Map())

/**
 * Derived atom: ordered list of session IDs (for list ordering)
 */
export const sessionIdsAtom = atom<string[]>([])

/**
 * Track which sessions have had their messages loaded (for lazy loading)
 * Sessions are loaded with empty messages initially, messages are fetched on-demand
 */
export const loadedSessionsAtom = atom<Set<string>>(new Set<string>())

/**
 * Promise cache for deduplicating concurrent session load requests.
 * Prevents race condition where multiple calls (e.g., from React re-renders)
 * start loading before the first completes and marks the session as loaded.
 * Module-level map since it tracks in-flight promises, not React state.
 */
const sessionLoadingPromises = new Map<string, Promise<Session | null>>()

/**
 * Currently active session ID - the session displayed in the main content area
 * This replaces the tab-based session selection
 */
export const activeSessionIdAtom = atom<string | null>(null)

function sessionMetaValuesEqual(left: unknown, right: unknown): boolean {
  if (Object.is(left, right)) return true
  if (Array.isArray(left) || Array.isArray(right)) {
    if (!Array.isArray(left) || !Array.isArray(right) || left.length !== right.length) return false
    return left.every((value, index) => sessionMetaValuesEqual(value, right[index]))
  }
  if (left === null || right === null || typeof left !== 'object' || typeof right !== 'object') return false
  const leftKeys = Object.keys(left)
  const rightKeys = Object.keys(right)
  if (leftKeys.length !== rightKeys.length) return false
  return leftKeys.every(key => Object.prototype.hasOwnProperty.call(right, key)
    && sessionMetaValuesEqual((left as Record<string, unknown>)[key], (right as Record<string, unknown>)[key]))
}

function hasSameSessionMeta(left: SessionMeta | undefined, right: SessionMeta): boolean {
  if (!left) return false

  const leftKeys = Object.keys(left) as Array<keyof SessionMeta>
  const rightKeys = Object.keys(right) as Array<keyof SessionMeta>
  if (leftKeys.length !== rightKeys.length) return false

  return rightKeys.every(key => sessionMetaValuesEqual(left[key], right[key]))
}

// NOTE: sessionsAtom REMOVED to fix memory leak
// The sessions array with messages was being retained by Jotai's internal state.
// Instead, we now use:
// - sessionMetaMapAtom for listing (lightweight metadata, no messages)
// - sessionAtomFamily(id) for individual session data
// - initializeSessionsAtom for bulk initialization
// - addSessionAtom, removeSessionAtom for individual operations

/**
 * Action atom: update a single session
 * Only triggers re-render in components subscribed to this specific session
 */
export const updateSessionAtom = atom(
  null,
  (get, set, sessionId: string, updater: (prev: Session | null) => Session | null) => {
    const sessionAtom = sessionAtomFamily(sessionId)
    const currentSession = get(sessionAtom)
    const newSession = updater(currentSession)
    const normalizedSession = newSession ? ensureSessionMessagesArray(newSession) : null
    set(sessionAtom, normalizedSession)

    // Keep list/board subscribers stable when an update only changes message content.
    if (normalizedSession) {
      const metaMap = get(sessionMetaMapAtom)
      const nextMeta = extractSessionMeta(normalizedSession)
      if (hasSameSessionMeta(metaMap.get(sessionId), nextMeta)) return

      const newMetaMap = new Map(metaMap)
      newMetaMap.set(sessionId, nextMeta)
      set(sessionMetaMapAtom, newMetaMap)
    }
  }
)

/**
 * Update live message/tool content without publishing a new list/board snapshot.
 * A terminal or metadata event must subsequently use updateSessionAtom so fields
 * such as messageCount and lastFinalMessageId become visible at a stable boundary.
 */
export const updateSessionContentAtom = atom(
  null,
  (get, set, sessionId: string, updater: (prev: Session | null) => Session | null) => {
    const sessionAtom = sessionAtomFamily(sessionId)
    const newSession = updater(get(sessionAtom))
    set(sessionAtom, newSession ? ensureSessionMessagesArray(newSession) : null)
  },
)

/**
 * Action atom: update only session metadata (for list display updates)
 * Doesn't affect the full session atom
 */
export const updateSessionMetaAtom = atom(
  null,
  (get, set, sessionId: string, updates: Partial<SessionMeta>) => {
    const metaMap = get(sessionMetaMapAtom)
    const existing = metaMap.get(sessionId)
    if (existing) {
      const newMetaMap = new Map(metaMap)
      newMetaMap.set(sessionId, { ...existing, ...updates })
      set(sessionMetaMapAtom, newMetaMap)
    }
  }
)

/** Keep the full session and lightweight board/list metadata on one status value. */
export const updateSessionStatusAtom = atom(
  null,
  (get, set, sessionId: string, sessionStatus: string | undefined) => {
    const sessionAtom = sessionAtomFamily(sessionId)
    const session = get(sessionAtom)
    if (session) set(sessionAtom, { ...session, sessionStatus })

    const metaMap = get(sessionMetaMapAtom)
    const existing = metaMap.get(sessionId)
    if (existing) {
      const next = new Map(metaMap)
      next.set(sessionId, { ...existing, sessionStatus })
      set(sessionMetaMapAtom, next)
    }
  },
)

/**
 * Action atom: append message to session (for streaming)
 * Optimized to only update the specific session
 * Note: Does NOT update lastMessageAt - caller must handle timestamp updates
 * to avoid session list jumping on intermediate/tool messages
 */
export const appendMessageAtom = atom(
  null,
  (get, set, sessionId: string, message: Message) => {
    const sessionAtom = sessionAtomFamily(sessionId)
    const session = get(sessionAtom)
    if (session) {
      set(sessionAtom, {
        ...session,
        messages: [...getSessionMessages(session), message],
        // Don't update lastMessageAt here - only user messages and final responses should update it
      })
    }
  }
)

/**
 * Action atom: update streaming content for a session
 * For text_delta events - appends to the last streaming message
 */
export const updateStreamingContentAtom = atom(
  null,
  (get, set, sessionId: string, content: string, turnId?: string) => {
    const sessionAtom = sessionAtomFamily(sessionId)
    const session = get(sessionAtom)
    if (!session) return

    const messages = [...getSessionMessages(session)]
    const lastMsg = messages[messages.length - 1]

    // Append to existing streaming message
    if (lastMsg?.role === 'assistant' && lastMsg.isStreaming &&
        (!turnId || lastMsg.turnId === turnId)) {
      messages[messages.length - 1] = {
        ...lastMsg,
        content: lastMsg.content + content,
      }
      set(sessionAtom, { ...session, messages })
    }
  }
)

/**
 * Action atom: initialize sessions from loaded data
 */
export const initializeSessionsAtom = atom(
  null,
  (get, set, sessions: Session[]) => {
    const normalizedSessions = sessions.map(ensureSessionMessagesArray)
    const previouslyLoaded = get(loadedSessionsAtom)
    const nextLoaded = new Set<string>()

    // Clean up stale atom family entries from previous workspace.
    // Without this, switching workspaces leaves orphaned atoms in memory
    // and components subscribed to old session IDs see stale/empty data.
    const oldIds = get(sessionIdsAtom)
    const newIdSet = new Set(normalizedSessions.map(s => s.id))
    for (const oldId of oldIds) {
      if (!newIdSet.has(oldId)) {
        sessionAtomFamily.remove(oldId)
        backgroundTasksAtomFamily.remove(oldId)
      }
    }
    // A duplicate initialization can resolve after an active session has already
    // lazy-loaded. Preserve that history for the same workspace; workspace switches
    // still receive fresh metadata-only atoms.
    const mergedSessions: Session[] = []
    for (const session of normalizedSessions) {
      const currentSession = get(sessionAtomFamily(session.id))
      const canPreserveLoadedMessages = previouslyLoaded.has(session.id)
        && currentSession?.workspaceId === session.workspaceId

      const incomingSession = canPreserveLoadedMessages && currentSession
        ? {
          ...session,
          messages: getSessionMessages(currentSession),
        }
        : session
      const mergedSession = mergeSessionSnapshot(currentSession, incomingSession)
      set(sessionAtomFamily(session.id), mergedSession)
      mergedSessions.push(mergedSession)

      if (canPreserveLoadedMessages) {
        nextLoaded.add(session.id)
      }
    }
    set(loadedSessionsAtom, nextLoaded)

    // Build metadata map
    const metaMap = new Map<string, SessionMeta>()
    for (const session of mergedSessions) {
      metaMap.set(session.id, extractSessionMeta(session))
    }
    set(sessionMetaMapAtom, metaMap)

    // Set ordered IDs (sorted by lastMessageAt desc)
    const ids = normalizedSessions
      .sort((a, b) => (b.lastMessageAt || 0) - (a.lastMessageAt || 0))
      .map(s => s.id)
    set(sessionIdsAtom, ids)

    // NOTE: Do NOT mark sessions as loaded here
    // Sessions from getSessions() have empty messages: [] to save memory
    // Messages are lazy-loaded via ensureSessionMessagesLoadedAtom when session is opened
    // This reduces initial memory usage from ~500MB to ~50MB for 300+ sessions
  }
)

/**
 * Action atom: refresh session metadata after a stale reconnect.
 *
 * Preserves messages for already-loaded sessions and clears loaded markers for
 * sessions whose metadata was refreshed without their full message history.
 * All cross-atom mutations happen in a single write transaction so subscribers
 * do not observe intermediate state.
 */
export const refreshSessionsMetadataAtom = atom(
  null,
  (
    get,
    set,
    payload: { sessions: Session[]; loadedSessionIds: Set<string> }
  ): Map<string, SessionMeta> => {
    const { loadedSessionIds } = payload
    const sessions = payload.sessions.map(ensureSessionMessagesArray)

    const currentIds = get(sessionIdsAtom)
    const latestIds = new Set(sessions.map(session => session.id))
    for (const staleSessionId of currentIds) {
      if (!latestIds.has(staleSessionId)) {
        set(removeSessionAtom, staleSessionId)
      }
    }

    const unloadedIds: string[] = []
    const mergedSessions: Session[] = []
    for (const session of sessions) {
      const currentSession = get(sessionAtomFamily(session.id))
      const shouldPreserveMessages = !!currentSession && loadedSessionIds.has(session.id)
      const incomingSession = shouldPreserveMessages && currentSession
        ? {
            ...session,
            messages: getSessionMessages(currentSession),
          }
        : session
      const nextSession = mergeSessionSnapshot(currentSession, incomingSession)
      mergedSessions.push(nextSession)

      set(sessionAtomFamily(session.id), nextSession)

      if (!shouldPreserveMessages && loadedSessionIds.has(session.id)) {
        unloadedIds.push(session.id)
      }
    }

    if (unloadedIds.length > 0) {
      const nextLoaded = new Set(get(loadedSessionsAtom))
      for (const id of unloadedIds) {
        nextLoaded.delete(id)
      }
      set(loadedSessionsAtom, nextLoaded)
    }

    const nextMetaMap = new Map<string, SessionMeta>()
    for (const session of mergedSessions) {
      nextMetaMap.set(session.id, extractSessionMeta(session))
    }
    set(sessionMetaMapAtom, nextMetaMap)

    const nextIds = sessions
      .slice()
      .sort((a, b) => (b.lastMessageAt || 0) - (a.lastMessageAt || 0))
      .map(session => session.id)
    set(sessionIdsAtom, nextIds)

    return nextMetaMap
  }
)

/**
 * Action atom: add a new session
 */
export const addSessionAtom = atom(
  null,
  (get, set, session: Session) => {
    const normalizedSession = ensureSessionMessagesArray(session)

    // Set session atom
    set(sessionAtomFamily(normalizedSession.id), normalizedSession)

    // Add to metadata map
    const metaMap = get(sessionMetaMapAtom)
    const newMetaMap = new Map(metaMap)
    newMetaMap.set(normalizedSession.id, extractSessionMeta(normalizedSession))
    set(sessionMetaMapAtom, newMetaMap)

    // Add to beginning of IDs list
    const ids = get(sessionIdsAtom)
    set(sessionIdsAtom, [normalizedSession.id, ...ids])

    // Mark as loaded (new sessions are complete - no lazy loading needed)
    const loadedSessions = get(loadedSessionsAtom)
    const newLoadedSessions = new Set(loadedSessions)
    newLoadedSessions.add(normalizedSession.id)
    set(loadedSessionsAtom, newLoadedSessions)
  }
)

/**
 * Action atom: remove a session
 */
export const removeSessionAtom = atom(
  null,
  (get, set, sessionId: string) => {
    // Clear session atom value first
    set(sessionAtomFamily(sessionId), null)
    // Remove atom from family cache to allow GC of the atom and its stored value
    sessionAtomFamily.remove(sessionId)

    // Remove from metadata map
    const metaMap = get(sessionMetaMapAtom)
    const newMetaMap = new Map(metaMap)
    newMetaMap.delete(sessionId)
    set(sessionMetaMapAtom, newMetaMap)

    // Remove from IDs list
    const ids = get(sessionIdsAtom)
    set(sessionIdsAtom, ids.filter(id => id !== sessionId))

    // Remove from loaded sessions tracking
    const loadedSessions = get(loadedSessionsAtom)
    const newLoadedSessions = new Set(loadedSessions)
    newLoadedSessions.delete(sessionId)
    set(loadedSessionsAtom, newLoadedSessions)

    // Clean up additional atom families to prevent memory leaks
    // These store per-session UI state that should be garbage collected
    backgroundTasksAtomFamily.remove(sessionId)
  }
)

/**
 * Action atom: sync React state to per-session atoms
 *
 * This is the key to the hybrid approach:
 * - React state (sessions array) remains the source of truth
 * - This atom syncs changes to per-session atoms automatically
 * - Components using useSession(id) get isolated updates
 * - Jotai's referential equality prevents unnecessary re-renders
 *
 * IMPORTANT: During streaming, the atom is the source of truth.
 * Streaming events (text_delta, tool_start, tool_result) update atoms directly
 * and bypass React state for performance. We must NOT overwrite atoms for
 * sessions that are processing, or we lose streaming data (tool calls, text).
 * Once a "handoff" event (complete, error, etc.) occurs, React state catches up
 * and sync works normally again.
 */
export const syncSessionsToAtomsAtom = atom(
  null,
  (get, set, sessions: Session[]) => {
    const normalizedSessions = sessions.map(ensureSessionMessagesArray)
    const loadedSessions = get(loadedSessionsAtom)

    // Update each session atom
    for (const session of normalizedSessions) {
      const sessionAtom = sessionAtomFamily(session.id)
      const atomSession = get(sessionAtom)

      // CRITICAL: If the atom's session is processing, it has streaming updates
      // that React state doesn't know about yet. Don't overwrite - atom is
      // source of truth during streaming. The handoff event will reconcile.
      if (atomSession?.isProcessing) {
        continue
      }

      // CRITICAL: If session messages were lazy-loaded, atom has full messages
      // but React state may have empty array. Only skip if React would lose messages.
      // Allow sync when React has MORE messages (e.g., user just sent a message).
      if (loadedSessions.has(session.id) && atomSession) {
        const atomMessageCount = getSessionMessages(atomSession).length
        const reactMessageCount = getSessionMessages(session).length
        // Skip sync only if React has fewer messages (would lose data)
        if (reactMessageCount < atomMessageCount) {
          continue
        }
      }

      // Only update if the session object is different (referential check)
      // This prevents unnecessary re-renders when the session hasn't changed
      if (atomSession !== session) {
        set(sessionAtom, session)
      }
    }

    // Update metadata map for list display
    // Note: We still update metadata from React state, which is fine because
    // metadata doesn't include messages - the streaming content we're protecting
    const metaMap = new Map<string, SessionMeta>()
    for (const session of normalizedSessions) {
      const meta = extractSessionMeta(session)
      // Preserve isProcessing from atom if atom is processing
      // React state may have stale isProcessing: false during streaming
      const atomSession = get(sessionAtomFamily(session.id))
      if (atomSession?.isProcessing) {
        meta.isProcessing = true
      }
      metaMap.set(session.id, meta)
    }
    set(sessionMetaMapAtom, metaMap)

    // Update ordered IDs (preserve order from React state)
    set(sessionIdsAtom, normalizedSessions.map(s => s.id))
  }
)

// loadedSessionsAtom moved up before sessionsAtom (needed for self-syncing)

/**
 * Action atom: Load session messages if not already loaded
 * Returns the loaded session or current session if already loaded.
 * Uses promise deduplication to prevent redundant IPC calls from concurrent requests.
 *
 * IMPORTANT: This only merges messages into the existing session atom.
 * UI state fields (hasUnread, isFlagged, sessionStatus, etc.) are preserved from
 * the in-memory atom, NOT overwritten with potentially stale disk data.
 * This prevents a race condition where optimistic updates (e.g., clearing the
 * NEW badge on session view) get clobbered by async message loading that reads
 * older state from disk.
 */
async function loadSessionMessages(
  get: Getter,
  set: Setter,
  sessionId: string,
  options?: { force?: boolean },
): Promise<Session | null> {
  const force = options?.force ?? false

  if (force) {
    const nextLoadedSessions = new Set(get(loadedSessionsAtom))
    nextLoadedSessions.delete(sessionId)
    set(loadedSessionsAtom, nextLoadedSessions)

    // Clear any stale in-flight request so the caller gets a fresh fetch.
    sessionLoadingPromises.delete(sessionId)
  } else {
    const loadedSessions = get(loadedSessionsAtom)

    // Already loaded, return current session
    if (loadedSessions.has(sessionId)) {
      return get(sessionAtomFamily(sessionId))
    }
  }

  // Check if already loading - return existing promise to deduplicate concurrent calls
  const existingPromise = sessionLoadingPromises.get(sessionId)
  if (existingPromise) {
    return existingPromise
  }

  // Create the loading promise with all the fetch and update logic
  const loadPromise = (async (): Promise<Session | null> => {
    // Fetch messages from main process
    const loadedSession = await window.electronAPI.getSessionMessages(sessionId)
    if (!loadedSession) {
      return get(sessionAtomFamily(sessionId))
    }

    // Merge messages and disk-only fields into existing session, preserving in-memory UI state.
    // The renderer's atom is authoritative for UI fields (hasUnread, isFlagged, etc.)
    // because optimistic updates may have changed them since the disk write.
    // tokenUsage and sessionFolderPath are only returned by getSession() (not getSessions()),
    // so they must be explicitly merged here to be available after app restart.
    const existingSession = get(sessionAtomFamily(sessionId))
    const existingMessages = existingSession ? getSessionMessages(existingSession) : []
    const loadedSessionHasMessages = Array.isArray(loadedSession.messages)
    const loadedMessages = getSessionMessages(loadedSession)
    const preservedStaleMessages = existingMessages.length > 0 && loadedMessages.length === 0
    const loadedSnapshotIsOlder = existingSession?.processingGeneration !== undefined
      && loadedSession.processingGeneration !== undefined
      && loadedSession.processingGeneration < existingSession.processingGeneration

    const snapshotMergedSession = mergeSessionSnapshot(existingSession, loadedSession)
    const mergedSession = existingSession
      ? {
          ...existingSession,
          parentSessionId: snapshotMergedSession.parentSessionId,
          rootSessionId: snapshotMergedSession.rootSessionId,
          sourceDelegationId: snapshotMergedSession.sourceDelegationId,
          delegationStatus: snapshotMergedSession.delegationStatus,
          delegationGoal: snapshotMergedSession.delegationGoal,
          delegationCreatedAt: snapshotMergedSession.delegationCreatedAt,
          delegationCompletedAt: snapshotMergedSession.delegationCompletedAt,
          delegationResultSummary: snapshotMergedSession.delegationResultSummary,
          ...getDelegationV2Fields(snapshotMergedSession),
          isProcessing: snapshotMergedSession.isProcessing,
          processingGeneration: snapshotMergedSession.processingGeneration,
          currentStatus: snapshotMergedSession.currentStatus,
          // Do not clobber in-memory messages when streaming is still active or when
          // lazy-load recovery returns an empty payload and the atom already has data.
          messages: snapshotMergedSession.isProcessing && existingMessages.length > 0
            ? existingMessages
            : preservedStaleMessages
              ? existingMessages
              : loadedMessages,
          tokenUsage: loadedSnapshotIsOlder
            ? existingSession.tokenUsage
            : loadedSession.tokenUsage ?? existingSession.tokenUsage,
          sessionFolderPath: loadedSession.sessionFolderPath ?? existingSession.sessionFolderPath,
        }
      : snapshotMergedSession
    set(sessionAtomFamily(sessionId), mergedSession)

    // Update lastFinalMessageId and delegation lifecycle metadata in the list.
    // Other UI fields are maintained through optimistic updates and IPC events.
    const lastFinalMessageId = loadedSnapshotIsOlder
      ? existingSession?.lastFinalMessageId
      : findLastFinalMessageId(loadedMessages)
    const metaMap = get(sessionMetaMapAtom)
    const existingMeta = metaMap.get(sessionId)
    if (existingMeta) {
      const nextMeta = {
        ...existingMeta,
        parentSessionId: mergedSession.parentSessionId,
        rootSessionId: mergedSession.rootSessionId,
        sourceDelegationId: mergedSession.sourceDelegationId,
        delegationStatus: mergedSession.delegationStatus,
        delegationGoal: mergedSession.delegationGoal,
        delegationCreatedAt: mergedSession.delegationCreatedAt,
        delegationCompletedAt: mergedSession.delegationCompletedAt,
        delegationResultSummary: mergedSession.delegationResultSummary,
        ...getDelegationV2Fields(mergedSession),
        isProcessing: mergedSession.isProcessing,
        processingGeneration: mergedSession.processingGeneration,
        ...(lastFinalMessageId ? { lastFinalMessageId } : {}),
      }
      const newMetaMap = new Map(metaMap)
      newMetaMap.set(sessionId, nextMeta)
      set(sessionMetaMapAtom, newMetaMap)
    }

    // Mark as loaded only when we received a fresh payload.
    // If we had to preserve stale in-memory messages because the backend returned
    // an empty array during lazy-load recovery, keep the session reloadable.
    if (loadedSessionHasMessages && !preservedStaleMessages) {
      const newLoadedSessions = new Set(get(loadedSessionsAtom))
      newLoadedSessions.add(sessionId)
      set(loadedSessionsAtom, newLoadedSessions)
    }

    return mergedSession
  })()

  // Cache the promise before awaiting
  sessionLoadingPromises.set(sessionId, loadPromise)

  try {
    return await loadPromise
  } finally {
    // Always clean up the cache, whether success or failure
    sessionLoadingPromises.delete(sessionId)
  }
}

export const ensureSessionMessagesLoadedAtom = atom(
  null,
  async (get, set, sessionId: string): Promise<Session | null> => {
    return loadSessionMessages(get, set, sessionId)
  }
)

/**
 * Force-refresh session messages even if the session is currently marked as loaded.
 * Used by reconnect recovery when a session atom is stuck in an empty-but-loaded state.
 */
export const forceSessionMessagesReloadAtom = atom(
  null,
  async (get, set, sessionId: string): Promise<Session | null> => {
    return loadSessionMessages(get, set, sessionId, { force: true })
  }
)

/**
 * Background task for ActiveTasksBar display
 */
export interface BackgroundTask {
  /** Task or shell ID */
  id: string
  /** Task type */
  type: 'agent' | 'shell'
  /** Tool use ID for correlation with messages */
  toolUseId: string
  /** When the task started */
  startTime: number
  /** Elapsed seconds (from progress events) */
  elapsedSeconds: number
  /** Task intent/description */
  intent?: string
  status?: 'running' | 'stale' | 'completed' | 'failed' | 'stopped' | 'orphaned'
  completedAt?: number
  lastSignalAt?: number
  outputFile?: string
  summary?: string
}

/**
 * Atom family for tracking active background tasks per session
 * Updated on task_backgrounded, shell_backgrounded, task_progress events
 * Cleared when tasks complete or are killed
 */
export const backgroundTasksAtomFamily = atomFamily(
  (_sessionId: string) => atom<BackgroundTask[]>([]),
  (a, b) => a === b
)

/**
 * Window's current workspace ID — shared between Root (ThemeProvider) and App.
 * Written by App on workspace switch, read by Root to keep the theme in sync.
 */
export const windowWorkspaceIdAtom = atom<string | null>(null)

/**
 * State for "Send to Workspace" dialog.
 * Set session IDs to open; clear to close.
 */
export const sendToWorkspaceAtom = atom<string[]>([])
